import { MessagePayload } from 'firebase/messaging';
import { useEffect, useState, useCallback } from 'react';

import { usePushNotification } from '@/shared/providers/push-notification/push-notification.provider';
import {
  initializeMessaging,
  getFCMToken,
  requestNotificationPermission,
  hasNotificationPermission,
  requestPermissionAndInitialize
} from '@/shared/services/utils/firebase/messaging.service';

interface UseFirebaseMessagingReturn {
  fcmToken: string | null;
  notificationPermission: NotificationPermission;
  isInitialized: boolean;
  isSupported: boolean;
  error: string | null;
  requestPermission: () => Promise<boolean>;
  requestPermissionAndInit: () => Promise<boolean>; // New method for Safari compatibility
  getToken: () => Promise<string | null>;
  registerToken: (userId?: string) => Promise<void>;
}

interface UseFirebaseMessagingOptions {
  onMessage?: (payload: MessagePayload) => void;
  onTokenReceived?: (token: string) => void;
  userId?: string;
  useCustomBanner?: boolean; // Use custom notification banner instead of browser notifications
  autoRegister?: boolean; // Auto-register with server when token is received
}

// Helper function to safely get notification permission
const getNotificationPermission = (): NotificationPermission => {
  if (typeof window === 'undefined') return 'default';
  if (!('Notification' in window)) return 'default';
  return Notification.permission;
};

export const useFirebaseMessaging = (
  options: UseFirebaseMessagingOptions = {}
): UseFirebaseMessagingReturn => {
  const {
    onMessage,
    onTokenReceived,
    userId,
    useCustomBanner = true,
    autoRegister = true
  } = options;
  const { showNotification } = usePushNotification();

  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>(
    getNotificationPermission()
  );
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initializationAttempted, setInitializationAttempted] = useState(false);

  // Check if notifications are supported
  useEffect(() => {
    const checkSupport = () => {
      const supported = 'Notification' in window && 'serviceWorker' in navigator;
      setIsSupported(supported);

      console.log('[useFirebaseMessaging] Notification support check:', {
        supported,
        hasNotification: 'Notification' in window,
        hasServiceWorker: 'serviceWorker' in navigator,
        permission: getNotificationPermission()
      });

      if (!supported) {
        setError('Notifications are not supported in this browser');
      }
    };

    checkSupport();
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      setError('Notifications are not supported in this browser');
      return false;
    }

    try {
      const granted = await requestNotificationPermission();
      setNotificationPermission(getNotificationPermission());

      if (!granted) {
        setError('Notification permission denied');
      } else {
        setError(null);
      }

      return granted;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to request permission';
      setError(errorMessage);
      return false;
    }
  }, [isSupported]);

  // Get FCM token
  const getToken = useCallback(async (): Promise<string | null> => {
    if (!isSupported) {
      setError('Notifications are not supported in this browser');
      return null;
    }

    try {
      const token = await getFCMToken();
      setFcmToken(token);

      if (token) {
        onTokenReceived?.(token);
      }

      return token;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get FCM token';
      setError(errorMessage);
      return null;
    }
  }, [isSupported, onTokenReceived]);

  // Request permission and initialize (Safari-compatible)
  const requestPermissionAndInit = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      setError('Notifications are not supported in this browser');
      return false;
    }

    try {
      setError(null);

      // Create message handler
      const messageHandler = (payload: MessagePayload) => {
        // Show custom banner if enabled
        if (useCustomBanner) {
          showNotification(payload);
        }

        // Call user's callback if provided
        if (onMessage) {
          onMessage(payload);
        }
      };

      // Request permission and initialize in one call (required for Safari)
      const unsubscribe = await requestPermissionAndInitialize(messageHandler, useCustomBanner);

      setNotificationPermission(getNotificationPermission());

      if (unsubscribe) {
        setIsInitialized(true);
        console.log('Firebase messaging initialized with permission');

        // Get token after successful initialization
        await getToken();

        return true;
      } else {
        setError('Failed to initialize messaging or permission denied');
        return false;
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to request permission and initialize';
      setError(errorMessage);
      return false;
    }
  }, [isSupported, onMessage, useCustomBanner, showNotification, getToken]);

  // Register token with server
  const registerToken = useCallback(async (userIdParam?: string): Promise<void> => {
    // Token registration is now handled automatically by the singleton messaging service
    console.log('Token registration is handled automatically by Firebase messaging service');
  }, []);

  // Initialize Firebase messaging - only if permission is already granted
  useEffect(() => {
    const initialize = async () => {
      const currentPermission = getNotificationPermission();

      console.log('[useFirebaseMessaging] Initialization attempt:', {
        isSupported,
        initializationAttempted,
        currentPermission,
        willInitialize: isSupported && !initializationAttempted && currentPermission === 'granted'
      });

      // Only initialize if supported and permission already granted
      if (!isSupported || initializationAttempted || currentPermission !== 'granted') {
        if (currentPermission !== 'granted') {
          console.log(
            '[useFirebaseMessaging] Skipping auto-initialization - permission not granted. Use NotificationPermissionBanner to prompt user.'
          );
        }
        return;
      }

      setInitializationAttempted(true);

      try {
        setError(null);

        // Create message handler
        const messageHandler = (payload: MessagePayload) => {
          // Show custom banner if enabled
          if (useCustomBanner) {
            showNotification(payload);
          }

          // Call user's callback if provided
          if (onMessage) {
            onMessage(payload);
          }
        };

        // Initialize messaging with the singleton pattern (no permission request)
        const unsubscribe = await initializeMessaging(messageHandler, useCustomBanner);

        if (unsubscribe) {
          setIsInitialized(true);
          console.log('Firebase messaging hook initialized successfully');

          // Get token since permission is already granted
          await getToken();
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to initialize messaging';
        setError(errorMessage);
        console.error('Error initializing Firebase messaging:', err);
      }
    };

    initialize();
  }, [
    isSupported,
    onMessage,
    getToken,
    useCustomBanner,
    showNotification,
    initializationAttempted
  ]);

  // Listen for permission changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setNotificationPermission(getNotificationPermission());
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return {
    fcmToken,
    notificationPermission,
    isInitialized,
    isSupported,
    error,
    requestPermission,
    requestPermissionAndInit,
    getToken,
    registerToken
  };
};
